import { Query } from 'components/reporting';
import { SharedAppConstants } from '@tock/shared-app-constants';
import { createWorkflowActions } from '@tock/shared/workflow/actionCreators';
import { dashboard } from 'types';
import { toInstant } from '@tock/shared/utils/time';
import protos from 'utils/protos';

const { ReportQueries, ReportQueriesResponse } = protos;

export const fetchReportsList = (
  charts: Query[],
  timeZone: string,
  internalRequest = false
): ApiAction => {
  const query: dashboard.IReportQuery[] = charts.map((chart) => ({
    reportName: chart.name ?? '',
    dimension: chart.dimensions.map((dimensionConfig) => dimensionConfig.dimension),
    metric: chart.metrics.map((metricConfig) => metricConfig.metric),
    filter: chart.filter,
    timeBuckets: {
      startInstantMs: toInstant(chart.start, timeZone).toEpochMilli(),
      endInstantMs: toInstant(chart.end, timeZone).toEpochMilli(),
      bucket: chart.bucketing,
      timeZone,
    },
  }));

  return {
    TockApi: {
      postProto: internalRequest ? '/internal/reportdata' : '/admin/reporting',
      type: SharedAppConstants.REPORTING_FETCH_ALL,
      request: ReportQueries.fromObject({ query }),
      onError: '', // Ignore reporting errors (not sure why we should?)
    },
  };
};

export const fetchReportsListAsync = (charts: Query[], timeZone: string): ApiAction => {
  const query: dashboard.IReportQuery[] = charts.map((chart) => ({
    reportName: chart.name ?? '',
    dimension: chart.dimensions.map((dimensionConfig) => dimensionConfig.dimension),
    metric: chart.metrics.map((metricConfig) => metricConfig.metric),
    filter: chart.filter,
    timeBuckets: {
      startInstantMs: toInstant(chart.start, timeZone).toEpochMilli(),
      endInstantMs: toInstant(chart.end, timeZone).toEpochMilli(),
      bucket: chart.bucketing,
      timeZone,
    },
  }));

  return {
    TockApi: {
      postProto: '/async/reporting',
      type: SharedAppConstants.REPORTING_FETCH_ALL_ASYNC,
      request: ReportQueries.fromObject({ query }),
      onError: '', // Ignore reporting errors (not sure why we should?)
    },
  };
};

export const { fetchWorkflowStatus, fetchWorkflowResult } = createWorkflowActions({
  statusType: SharedAppConstants.REPORTING_FETCH_WORKFLOW,
  resultType: SharedAppConstants.REPORTING_FETCH_WORKFLOW_RESULTS_DONE,
  errorType: SharedAppConstants.REPORTING_FETCH_WORKFLOW_FAILED,
  protoType: ReportQueriesResponse,
});

export function selectBusinessFilter(businessFilter: string): BaseTockAction {
  return {
    type: SharedAppConstants.REPORTING_SELECT_BUSINESS_FILTER,
    payload: businessFilter,
  };
}

export function setTicketTypeFilter(filterValue: string, ticketTypeIds: number[]): BaseTockAction {
  return {
    type: SharedAppConstants.REPORTING_SELECT_TICKET_TYPE_FILTER,
    payload: { filter: filterValue, ticketTypeIds },
  };
}

export function selectMetricFilter(metricFilter: string): BaseTockAction {
  return {
    type: SharedAppConstants.REPORTING_SELECT_METRIC_FILTER,
    payload: metricFilter,
  };
}

export function setDateRangeFilter(start: string, end: string): BaseTockAction {
  return {
    type: SharedAppConstants.REPORTING_SELECT_DATE_RANGE_FILTER,
    payload: { start, end },
  };
}

export function clear(): BaseTockAction {
  return {
    type: SharedAppConstants.REPORTING_CLEAR,
  };
}
